import { type NuxtApp, useNuxtApp } from 'nuxt/app'
import { getCurrentScope } from 'vue'

import type { DIContainer } from './di'
import type { HttpClient, SocketClient } from './transports'

export type { DIContainer } from './di'

import useAnnouncement from './domains/announcement/use-case'
import useRoot from './domains/root/use-case'
import useUser from './domains/user'
import { storageService } from './services'

type DIPayload = {
  httpClient: HttpClient
  localeInst: ReturnType<typeof useLocale>
  route: ReturnType<typeof useRoute>
  router: ReturnType<typeof useRouter>
  socketClient: SocketClient
}

type DomainName = Exclude<keyof DIContainer, 'announcement' | 'root' | 'user'>

// Keep these imports dynamic: a static import would put the domain back in the entry bundle.
const loaders = {
  advent: () => import('./domains/advent/use-case'),
  aviaspin: () => import('./domains/aviaspin/use-case'),
  baccarat: () => import('./domains/baccarat/use-case'),
  banner: () => import('./domains/banner/use-case'),
  bonus: () => import('./domains/bonus/use-case'),
  castDev: () => import('./domains/cast-dev/use-case'),
  classic: () => import('./domains/classic/use-case'),
  coinflip: () => import('./domains/coinflip/use-case'),
  crash: () => import('./domains/crash/use-case'),
  dice: () => import('./domains/dice/use-case'),
  double: () => import('./domains/double/use-case'),
  event: () => import('./domains/event/use-case'),
  faq: () => import('./domains/faq/use-case'),
  feedback: () => import('./domains/feedback/use-case'),
  fortuneWheel: () => import('./domains/fortune-wheel/use-case'),
  game: () => import('./domains/game/use-case'),
  generator: () => import('./domains/generator/use-case'),
  giveaway: () => import('./domains/giveaway/use-case'),
  jackpot: () => import('./domains/jackpot/use-case'),
  keno: () => import('./domains/keno/use-case'),
  kycAid: () => import('./domains/kycaid/use-case'),
  leaders: () => import('./domains/leaders/use-case'),
  livechat: () => import('./domains/livechat/use-case'),
  liveResult: () => import('./domains/live-result/use-case'),
  liveStats: () => import('./domains/live-stats/use-case'),
  originalFreeSpins: () => import('./domains/original-freespins/use-case'),
  pay: () => import('./domains/pay/use-case'),
  plinko: () => import('./domains/plinko/use-case'),
  providerGame: () => import('./domains/provider-game/use-case'),
  rewards: () => import('./domains/rewards/use-case'),
  rocket: () => import('./domains/rocket/use-case'),
  roulette: () => import('./domains/roulette/use-case'),
  sports: () => import('./domains/sports/use-case'),
  tournament: () => import('./domains/tournament/use-case'),
  tower: () => import('./domains/tower/use-case'),
  upgrader: () => import('./domains/upgrader/use-case'),
  vip: () => import('./domains/vip/use-case'),
  wheel: () => import('./domains/wheel/use-case'),
} satisfies Record<DomainName, () => Promise<unknown>>

// SSR requests must not share clients or domain instances.
const containers = new WeakMap<NuxtApp, DIContainer>()

export function createDIContainer(payload: DIPayload, app: NuxtApp = useNuxtApp()): DIContainer {
  const { httpClient, localeInst, route, router, socketClient } = payload
  const rootStore = useBrickRootStore()
  const userStore = useBrickUserStore()
  const tournamentsStore = useBrickTournamentsStore()
  const vipStore = useBrickVipStore()
  const currency = useCurrency()
  const dialog = useDialog()
  const notify = useNotify()

  // Keep startup dependencies eager so root/user behavior stays synchronous before the first await.
  const root = useRoot({
    currency,
    dialog,
    httpClient,
    localeInst,
    rootStore,
    route,
    router,
    socketClient,
    tournamentsStore,
    userStore,
    vipStore,
  })

  const user = useUser({
    httpClient,
    localeInst,
    notify,
    root,
    route,
    router,
    storageService,
    userStore,
  })

  const domains = new Map<DomainName, Promise<DIContainer[DomainName]>>()

  function loadDomain<Name extends DomainName>(name: Name): Promise<DIContainer[Name]> {
    const existing = domains.get(name)
    if (existing) {
      return existing as Promise<DIContainer[Name]>
    }

    async function initialize(): Promise<DIContainer[DomainName]> {
      try {
        const module = await loaders[name]()
        return app.runWithContext(() => module.default(payload))
      } catch (error: unknown) {
        // Allow another call to retry a failed import or initialization.
        domains.delete(name)
        throw error
      }
    }

    const pending = initialize()

    domains.set(name, pending)
    return pending as Promise<DIContainer[Name]>
  }

  function createDomainProxy<Name extends DomainName>(name: Name): DIContainer[Name] {
    const methods = new Map<string, (...args: unknown[]) => Promise<unknown>>()

    return new Proxy(Object.create(null) as DIContainer[Name], {
      get(_target, method: string | symbol) {
        // Do not accidentally make a domain a thenable (await / Promise.resolve).
        if (typeof method !== 'string' || method === 'then') {
          return undefined
        }

        let invoke = methods.get(method)
        if (!invoke) {
          invoke = async (...args: unknown[]) => {
            // Capture the caller's scope before import() yields.
            const scope = getCurrentScope()
            const domain = await loadDomain(name)

            if (scope && !scope.active) {
              throw new DOMException(`DI call cancelled: ${name}.${method}`, 'AbortError')
            }

            const call = (): unknown =>
              app.runWithContext(() => {
                const handler = Reflect.get(domain, method) as unknown
                if (typeof handler !== 'function') {
                  throw new TypeError(`Unknown DI method: ${name}.${method}`)
                }
                return Reflect.apply(handler, domain, args) as unknown
              })

            // runWithContext restores Nuxt context; scope.run restores Vue cleanup ownership.
            return scope ? scope.run(call) : call()
          }
          methods.set(method, invoke)
        }
        return invoke
      },
    })
  }

  return {
    advent: createDomainProxy('advent'),
    // These fire-and-forget methods return void; keep their original synchronous invocation.
    announcement: useAnnouncement({ httpClient }),
    aviaspin: createDomainProxy('aviaspin'),
    baccarat: createDomainProxy('baccarat'),
    banner: createDomainProxy('banner'),
    bonus: createDomainProxy('bonus'),
    castDev: createDomainProxy('castDev'),
    classic: createDomainProxy('classic'),
    coinflip: createDomainProxy('coinflip'),
    crash: createDomainProxy('crash'),
    dice: createDomainProxy('dice'),
    double: createDomainProxy('double'),
    event: createDomainProxy('event'),
    faq: createDomainProxy('faq'),
    feedback: createDomainProxy('feedback'),
    fortuneWheel: createDomainProxy('fortuneWheel'),
    game: createDomainProxy('game'),
    generator: createDomainProxy('generator'),
    giveaway: createDomainProxy('giveaway'),
    jackpot: createDomainProxy('jackpot'),
    keno: createDomainProxy('keno'),
    kycAid: createDomainProxy('kycAid'),
    leaders: createDomainProxy('leaders'),
    livechat: createDomainProxy('livechat'),
    liveResult: createDomainProxy('liveResult'),
    liveStats: createDomainProxy('liveStats'),
    originalFreeSpins: createDomainProxy('originalFreeSpins'),
    pay: createDomainProxy('pay'),
    plinko: createDomainProxy('plinko'),
    providerGame: createDomainProxy('providerGame'),
    rewards: createDomainProxy('rewards'),
    rocket: createDomainProxy('rocket'),
    root,
    roulette: createDomainProxy('roulette'),
    sports: createDomainProxy('sports'),
    tournament: createDomainProxy('tournament'),
    tower: createDomainProxy('tower'),
    upgrader: createDomainProxy('upgrader'),
    user,
    vip: createDomainProxy('vip'),
    wheel: createDomainProxy('wheel'),
  }
}

export function getDIContainer(app: NuxtApp = useNuxtApp()): DIContainer {
  const container = containers.get(app)
  if (!container) {
    throw new Error('Lazy DI has not been initialized for this Nuxt app')
  }
  return container
}

export function initDIContainer(payload: DIPayload, app: NuxtApp = useNuxtApp()): DIContainer {
  const existing = containers.get(app)
  if (existing) {
    return existing
  }

  const container = createDIContainer(payload, app)
  containers.set(app, container)
  return container
}
